'use client';

import { useRef } from 'react';
import { EmbedFrame } from '../EmbedFrame';
import {
  EVERVIZ_ORIGIN,
  evervizEmbedUrl,
  evervizFrameHeight,
  resolveEvervizId,
} from '../providers';
import { useEmbedAutoHeight } from '../useEmbedAutoHeight';

interface EvervizEmbedProps {
  src?: string;
  url?: string;
  id?: string;
  title?: string;
  ratio?: string;
}

/**
 * Everviz is the most common BC embed. We render it iframe-first
 * (app.everviz.com/embed/<id>) so the dominant case loads no third-party
 * script and needs no consent gate.
 *
 * Everviz charts are responsive: the embed iframe reports its rendered height
 * (chart chrome + plot) via `postMessage`. We listen for that and size the
 * frame to the reported height so charts aren't clipped on narrow viewports
 * (matching the live inject-script behavior). Until the first message arrives
 * we render an aspect-ratio frame to avoid a layout jump.
 */
export function EvervizEmbed({
  src,
  url,
  id,
  title,
  ratio,
}: EvervizEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const height = useEmbedAutoHeight({
    iframeRef,
    origin: EVERVIZ_ORIGIN,
    getHeight: evervizFrameHeight,
  });

  const chartId = resolveEvervizId(id ?? src ?? url);
  if (!chartId) return null;

  const iframe = (
    <iframe
      ref={iframeRef}
      className='h-full w-full border-0'
      src={evervizEmbedUrl(chartId)}
      title={title ?? 'Everviz data visualization'}
      loading='lazy'
      scrolling='no'
    />
  );

  if (height != null) {
    return (
      <div className='my-lg w-full' style={{ height }}>
        {iframe}
      </div>
    );
  }

  return <EmbedFrame ratio={ratio}>{iframe}</EmbedFrame>;
}
